/* FOM · renderizador do mapa vivo (SVG).

   Decisoes de renderizacao, e o motivo de cada uma:

   · SVG, nao canvas — os nos precisam ser alcancaveis por teclado e por
     seletor. Com 154 nos o custo e irrelevante e o ganho de acessibilidade e
     de teste e grande.

   · O layout so e recalculado quando a ARVORE muda (nos, expansao, filtro).
     Clicar para selecionar nao recalcula nada: troca uma classe. O mapa do
     artifact remedia tudo a cada interacao — aqui isso seria desperdicio
     visivel quando o mapa crescer.

   · Sem animacao decorativa. A unica transicao e a de foco/selecao, que serve
     para o olho nao perder o no. */
import type { FomNode, Relation, StatusClass } from '../core/types.ts';
import { COR_ST, rotuloTipo} from '../core/types.ts';

/* Agregado de uma subarvore — dado REAL somado dos descendentes ativos
   (para a capsula do zoom semantico). Puro e deterministico. */
export type Agregado = {
  total: number;
  know: number;
  gaps: number;
  tobe: number;
  st: Partial<Record<StatusClass, number>>;
};
export function agregarSubarvores(nos: FomNode[]): Map<string, Agregado> {
  const filhos = new Map<string, FomNode[]>();
  for (const n of nos) {
    if (n.removed_at || !n.parent_external_id) continue;
    const l = filhos.get(n.parent_external_id) ?? [];
    l.push(n);
    filhos.set(n.parent_external_id, l);
  }
  const memo = new Map<string, Agregado>();
  const de = (id: string, prof: number): Agregado => {
    const pronto = memo.get(id);
    if (pronto) return pronto;
    const a: Agregado = { total: 0, know: 0, gaps: 0, tobe: 0, st: {} };
    if (prof < 64) {
      for (const f of filhos.get(id) ?? []) {
        const sub = de(f.external_id, prof + 1);
        a.total += 1 + sub.total;
        a.know += f.know.length + sub.know;
        a.gaps += f.gaps.length + sub.gaps;
        a.tobe += (ehFuturo(f) ? 1 : 0) + sub.tobe;
        a.st[f.st] = (a.st[f.st] ?? 0) + 1;
        for (const k of Object.keys(sub.st) as StatusClass[]) a.st[k] = (a.st[k] ?? 0) + (sub.st[k] ?? 0);
      }
    }
    memo.set(id, a);
    return a;
  };
  for (const n of nos) if (!n.removed_at) de(n.external_id, 0);
  return memo;
}
import { calcularLayout, type Layout, type OpcoesLayout } from '../core/layout.ts';
import { ehFuturo, type Lente } from '../core/lentes.ts';
import { seloDe } from '../dominio/apresentacao.ts';
import { ehProduto } from '../dominio/dimensoes.ts';

const NS = 'http://www.w3.org/2000/svg';

export type MapaOpcoes = {
  aoSelecionar: (externalId: string | null) => void;
  /* o chrome (nivel de zoom na barra lateral) acompanha sem consultar */
  aoZoom?: (zoom: number) => void;
  /* ZOOM SEMANTICO (next-gen): ao afastar alem do piso de legibilidade, o
     chrome pode responder mudando a REPRESENTACAO (recolher um nivel em
     capsulas) em vez de encolher os cartoes. Retorna true se tratou. */
  aoZoomSemantico?: (direcao: 'afastar' | 'aproximar') => boolean;
  aoAlternarExpansao: (externalId: string) => void;
  aoLigar: (de: string, para: string) => void;
  /* CONEXAO como objeto de primeira classe (gate 0072): clicar na linha de
     uma relacao semantica seleciona a relacao (id). Arestas pai/filho nao
     sao selecionaveis — hierarquia nao e relacao. */
  aoSelecionarRelacao?: (id: string | null) => void;
  /* Chamado ao SOLTAR o no, com o deslocamento acumulado. Nao e chamado a
     cada movimento: uma escrita por arrasto, nao uma por pixel. */
  aoMoverNo?: (externalId: string, dx: number, dy: number) => void;
};

export type EntradaRender = {
  nos: FomNode[];
  relacoes: Relation[];
  selecionado: string | null;
  ligando: string | null;
  layout: OpcoesLayout;
  /* relacao selecionada (gate 0072) — realce da linha e das duas pontas */
  relacaoSelecionada?: string | null;
};

function el<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const n = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
}

/* Sem CSS.escape: os external_id do FOM sao [a-z0-9-~], e depender de uma API
   de browser aqui impediria testar o renderizador fora do navegador. */
function seletorPorId(id: string): string {
  const seguro = id.replace(/[^A-Za-z0-9_~.-]/g, "");
  return '[data-id="' + seguro + '"]';
}

function curva(x1: number, y1: number, x2: number, y2: number): string {
  const meio = (y1 + y2) / 2;
  return `M ${x1} ${y1} C ${x1} ${meio}, ${x2} ${meio}, ${x2} ${y2}`;
}

/* RELACAO (gate 0072, gramatica do artefato): arco QUADRATICO por CIMA dos
   cartoes — sobe 40px acima do topo mais alto, mais 8% da distancia
   horizontal, de modo que arcos longos passem por cima dos curtos. A
   hierarquia continua sendo a curva em S vertical: as duas leituras nunca se
   confundem. */
function arco(ax: number, ay: number, ah: number, bx: number, by: number, bh: number): string {
  const mx = (ax + bx) / 2;
  const my = Math.min(ay, by) - 40 - Math.abs(ax - bx) * 0.08;
  return `M${ax} ${ay + ah / 2} Q${mx} ${my} ${bx} ${by + bh / 2}`;
}

export function criarMapa(host: HTMLElement, opcoes: MapaOpcoes) {
  host.innerHTML = '';

  const svg = el('svg', { class: 'mapa', width: '100%', height: '100%' });
  const defs = el('defs');
  const seta = el('marker', {
    id: 'seta', viewBox: '0 0 10 10', refX: 9, refY: 5,
    markerWidth: 7, markerHeight: 7, orient: 'auto',
  });
  seta.appendChild(el('path', { d: 'M 0 0 L 10 5 L 0 10 z', class: 'seta' }));
  defs.appendChild(seta);
  /* fundo do palco: malha de pontos de 28px (artefato) — textura de
     "espaco", nunca informacao. Fica FORA do grupo transformado para nao
     escalar com o zoom? Nao: escala junto, e assim o ponto ancora a nocao
     de distancia enquanto a camera se move. */
  const pontos = el('pattern', { id: 'malha-pontos', width: 28, height: 28, patternUnits: 'userSpaceOnUse' });
  pontos.appendChild(el('circle', { cx: 1, cy: 1, r: 1, class: 'malha-ponto' }));
  defs.appendChild(pontos);
  svg.appendChild(defs);

  const palco = el('g', { class: 'palco' });
  palco.appendChild(el('rect', {
    class: 'malha-fundo', x: -20000, y: -20000, width: 40000, height: 40000,
    fill: 'url(#malha-pontos)',
  }));
  const gArestas = el('g', { class: 'arestas' });
  const gRelacoes = el('g', { class: 'relacoes' });
  const gNos = el('g', { class: 'nos' });
  palco.appendChild(gArestas);
  palco.appendChild(gRelacoes);
  palco.appendChild(gNos);
  svg.appendChild(palco);
  host.appendChild(svg);

  let zoom = 1, panX = 0, panY = 0;
  let layoutAtual: Layout | null = null;
  let assinaturaArvore = '';
  let selecionadoAtual: string | null = null;
  let relacoesAtuais: Relation[] = [];
  let relacaoSelecionadaAtual: string | null = null;
  let agregadosAtuais: Map<string, Agregado> = new Map();
  /* abaixo deste zoom o texto de um cartao deixa de ser legivel: e aqui que
     o zoom semantico troca a representacao em vez da escala */
  const ZOOM_SEMANTICO = 0.6;
  /* para a relacao recem-criada "nascer" desenhando-se */
  let idsRelacoesVistas = new Set<string>();

  function aplicarTransform(): void {
    palco.setAttribute('transform', `translate(${panX} ${panY}) scale(${zoom})`);
    /* ZOOM SEMANTICO: a informacao aparece conforme a aproximacao.
       longe  -> so estrutura e titulos (o "mapa de areas");
       medio  -> titulos + indicadores de estado;
       perto  -> tudo, inclusive codigo e a linha de metadados.
       E CSS por atributo: nenhum re-render, nenhum custo por frame. */
    const nivel = zoom < 0.45 ? 'longe' : zoom < 0.95 ? 'medio' : 'perto';
    if (svg.getAttribute('data-zoom') !== nivel) svg.setAttribute('data-zoom', nivel);
    opcoes.aoZoom?.(zoom);
    pintarMinimapaViewport();
  }

  /* ------------------------------------------------------------- minimapa --
     Visao do todo num canto: retangulos por no (sem texto — e um sensor de
     posicao, nao um segundo mapa) e o retangulo da janela atual. Clicar
     salta para la. Redesenhado so quando a ARVORE muda; a janela, a cada
     transform — dois retangulos, custo nenhum. */
  const MINI_W = 176, MINI_H = 112;
  const mini = document.createElement('div');
  mini.className = 'minimapa';
  mini.setAttribute('aria-label', 'Visao geral do mapa');
  const miniAlternar = document.createElement('button');
  miniAlternar.className = 'minimapa-alternar';
  miniAlternar.title = 'Recolher/expandir o minimapa';
  miniAlternar.textContent = '▾';
  miniAlternar.addEventListener('pointerdown', (ev) => ev.stopPropagation());
  miniAlternar.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const recolhido = mini.classList.toggle('minimapa-recolhido');
    miniAlternar.textContent = recolhido ? '▴' : '▾';
  });
  mini.appendChild(miniAlternar);
  const miniSvg = el('svg', { width: MINI_W, height: MINI_H, class: 'minimapa-svg' });
  const miniNos = el('g', { class: 'minimapa-nos' });
  const miniJanela = el('rect', { class: 'minimapa-janela', x: 0, y: 0, width: 10, height: 10 });
  /* RADAR (1AD.2): o marcador de POSICAO — "onde estou dentro da org" */
  const miniMarcador = el('circle', { class: 'minimapa-selecao', r: 3.4, cx: -10, cy: -10 });
  miniSvg.appendChild(miniNos);
  miniSvg.appendChild(miniJanela);
  miniSvg.appendChild(miniMarcador);
  mini.appendChild(miniSvg);
  host.appendChild(mini);

  function escalaMini(): { k: number; ox: number; oy: number } | null {
    if (!layoutAtual) return null;
    const e = layoutAtual.extensao;
    const k = Math.min(MINI_W / Math.max(e.w, 1), MINI_H / Math.max(e.h, 1));
    return { k, ox: (MINI_W - e.w * k) / 2 - e.x * k, oy: (MINI_H - e.h * k) / 2 - e.y * k };
  }

  function pintarMinimapaNos(): void {
    miniNos.textContent = '';
    const m = escalaMini();
    if (!m || !layoutAtual) return;
    for (const p of layoutAtual.posicoes) {
      miniNos.appendChild(el('rect', {
        class: 'minimapa-no' + (p.no.origin === 'user' ? ' minimapa-no-user' : '')
          + (ehFuturo(p.no) ? ' minimapa-no-futuro' : ''),
        x: (p.x - p.w / 2) * m.k + m.ox,
        y: p.y * m.k + m.oy,
        width: Math.max(2, p.w * m.k),
        height: Math.max(1.6, p.h * m.k),
        rx: 1,
      }));
    }
  }

  function pintarMinimapaViewport(): void {
    const m = escalaMini();
    if (!m) return;
    const caixa = svg.getBoundingClientRect();
    const vx = -panX / zoom, vy = -panY / zoom;
    const vw = (caixa.width || 1200) / zoom, vh = (caixa.height || 800) / zoom;
    miniJanela.setAttribute('x', String(vx * m.k + m.ox));
    miniJanela.setAttribute('y', String(vy * m.k + m.oy));
    miniJanela.setAttribute('width', String(Math.max(6, vw * m.k)));
    miniJanela.setAttribute('height', String(Math.max(6, vh * m.k)));
  }

  mini.addEventListener('pointerdown', (ev) => {
    const m = escalaMini();
    if (!m) return;
    const r = mini.getBoundingClientRect();
    const lx = (ev.clientX - r.left - m.ox) / m.k;
    const ly = (ev.clientY - r.top - m.oy) / m.k;
    const caixa = svg.getBoundingClientRect();
    panX = (caixa.width || 1200) / 2 - lx * zoom;
    panY = (caixa.height || 800) / 2 - ly * zoom;
    aplicarTransform();
  });

  /* ------------------------------------------------------------ navegacao -- */
  let arrastando = false;
  let ultimoX = 0, ultimoY = 0;

  /* Arrasto de NO — distinto do arrasto do MAPA. O no so entra em modo de
     arrasto depois de um limiar de alguns pixels: sem isso, todo clique de
     selecao viraria um micro-movimento e geraria escrita a toa. */
  const LIMIAR = 4;
  let ligandoAtual: string | null = null;
  let noArrastado: { id: string; g: SVGGElement; x0: number; y0: number;
                     dx: number; dy: number; efetivo: boolean } | null = null;

  svg.addEventListener('pointerdown', (ev) => {
    const alvo = ev.target as Element;
    const cartao = alvo.closest('.no');
    if (cartao) {
      /* durante a ligacao o cartao e alvo de clique, nao de arrasto */
      if (ligandoAtual !== null || !opcoes.aoMoverNo) return;
      const id = cartao.getAttribute('data-id');
      if (!id) return;
      noArrastado = {
        id, g: cartao as SVGGElement,
        x0: ev.clientX, y0: ev.clientY, dx: 0, dy: 0, efetivo: false,
      };
      /* SEM capturar o ponteiro aqui. Capturar neste momento re-alveja o
         pointerup para o svg e o `click` nasce no svg — o handler do no nunca
         dispara e clicar passa a DESSELECIONAR. A captura acontece so quando o
         movimento cruza o limiar e vira arrasto de verdade (no pointermove). */
      return;
    }
    arrastando = true;
    ultimoX = ev.clientX;
    ultimoY = ev.clientY;
    svg.setPointerCapture(ev.pointerId);
    svg.classList.add('arrastando');
  });
  svg.addEventListener('pointermove', (ev) => {
    if (noArrastado) {
      const bx = ev.clientX - noArrastado.x0;
      const by = ev.clientY - noArrastado.y0;
      if (!noArrastado.efetivo && Math.hypot(bx, by) < LIMIAR) return;
      if (!noArrastado.efetivo) {
        /* virou arrasto: agora sim o svg captura, para o movimento nao escapar */
        try { svg.setPointerCapture(ev.pointerId); } catch { /* ponteiro ja foi */ }
      }
      noArrastado.efetivo = true;
      /* o palco esta em escala: o deslocamento na tela nao e o do modelo */
      noArrastado.dx = bx / zoom;
      noArrastado.dy = by / zoom;
      const p = layoutAtual?.porId.get(noArrastado.id);
      if (p) {
        noArrastado.g.setAttribute(
          'transform',
          `translate(${p.x - p.w / 2 + noArrastado.dx} ${p.y + noArrastado.dy})`,
        );
        noArrastado.g.classList.add('no-arrastando');
      }
      return;
    }
    if (!arrastando) return;
    panX += ev.clientX - ultimoX;
    panY += ev.clientY - ultimoY;
    ultimoX = ev.clientX;
    ultimoY = ev.clientY;
    aplicarTransform();
  });
  const soltar = (ev: PointerEvent): void => {
    if (noArrastado) {
      const { id, dx, dy, efetivo, g } = noArrastado;
      noArrastado = null;
      g.classList.remove('no-arrastando');
      try { svg.releasePointerCapture(ev.pointerId); } catch { /* ja liberado */ }
      /* movimento abaixo do limiar foi clique, nao arrasto: nada a persistir */
      if (efetivo) {
        /* o delta persistido e medido a partir do lugar ESTRUTURAL + ajuste:
           se o motor tinha afastado este no por conflito (`desvio`), esse
           afastamento entra no delta — o no fica exatamente onde foi solto,
           em vez de pular de volta quando o conflito deixa de existir */
        const p = layoutAtual?.porId.get(id);
        opcoes.aoMoverNo?.(id, Math.round(dx + (p?.desvio.x ?? 0)), Math.round(dy + (p?.desvio.y ?? 0)));
      }
      return;
    }
    if (!arrastando) return;
    arrastando = false;
    try { svg.releasePointerCapture(ev.pointerId); } catch { /* ponteiro ja liberado */ }
    svg.classList.remove('arrastando');
  };
  svg.addEventListener('pointerup', soltar);
  svg.addEventListener('pointercancel', soltar);

  svg.addEventListener('wheel', (ev) => {
    ev.preventDefault();
    const caixa = svg.getBoundingClientRect();
    const mx = ev.clientX - caixa.left;
    const my = ev.clientY - caixa.top;
    const fator = ev.deltaY < 0 ? 1.12 : 1 / 1.12;
    /* afastar alem do piso legivel: primeiro tenta mudar a representacao */
    if (fator < 1 && zoom * fator < ZOOM_SEMANTICO && zoom >= ZOOM_SEMANTICO
      && opcoes.aoZoomSemantico?.('afastar') === true) return;
    const novo = Math.min(2.5, Math.max(0.12, zoom * fator));
    /* zoom ancorado no ponteiro: o que esta sob o cursor nao escapa */
    panX = mx - ((mx - panX) * novo) / zoom;
    panY = my - ((my - panY) * novo) / zoom;
    zoom = novo;
    aplicarTransform();
  }, { passive: false });

  svg.addEventListener('click', (ev) => {
    if ((ev.target as Element).closest('.no')) return;
    /* clique na LINHA de uma relacao: seleciona a conexao (gate 0072). O
       alvo largo (.relacao-alvo) existe para o dedo/ponteiro acertar. */
    const grupoRel = (ev.target as Element).closest('.relacao-grupo') as SVGGElement | null;
    if (grupoRel && opcoes.aoSelecionarRelacao) {
      ev.stopPropagation();
      opcoes.aoSelecionarRelacao(grupoRel.dataset.relacao ?? null);
      return;
    }
    opcoes.aoSelecionar(null);
  });

  /* handlers DELEGADOS dos nos — um par para o mapa inteiro (1AD.1) */
  gNos.addEventListener('click', (ev) => {
    const alvo = ev.target as Element;
    const noEl = alvo.closest('.no') as SVGGElement | null;
    if (!noEl) return;
    ev.stopPropagation();
    const id = noEl.dataset.id!;
    if (alvo.closest('[data-alternar]')) {
      opcoes.aoAlternarExpansao(id);
      return;
    }
    if (ligandoAtual && ligandoAtual !== id) {
      opcoes.aoLigar(ligandoAtual, id);
      return;
    }
    opcoes.aoSelecionar(id);
  });
  gNos.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const noEl = (e.target as Element).closest('.no') as SVGGElement | null;
    if (!noEl) return;
    e.preventDefault();
    opcoes.aoSelecionar(noEl.dataset.id!);
  });
  /* teclado tambem seleciona conexoes (gate 0072) */
  gRelacoes.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const g = (e.target as Element).closest('.relacao-grupo') as SVGGElement | null;
    if (!g || !opcoes.aoSelecionarRelacao) return;
    e.preventDefault();
    opcoes.aoSelecionarRelacao(g.dataset.relacao ?? null);
  });

  /* --------------------------------------------------------------- render -- */
  function desenharNo(p: Layout['posicoes'][number], ligando: string | null): SVGGElement {
    const { no } = p;
    const g = el('g', {
      class: 'no' + (no.origin === 'user' ? ' no-user' : '')
        + (no.child_kind === 'flow' ? ' no-fluxo' : ''),
      transform: `translate(${p.x - p.w / 2} ${p.y})`,
      'data-id': no.external_id,
      'data-prof': Math.min(no.depth ?? 0, 4),
      'data-tipo': no.node_type ?? 'nd',
      tabindex: 0,
      role: 'button',
      'aria-label': `${no.name} — ${no.st}`,
    });

    /* ANATOMIA DO CARTAO (gate 0072, artefato): faixa epistemica como linha
       curta a esquerda, nome em UMA linha (tooltip/painel tem o inteiro),
       linha de tipo "ST · TIPO" e linha de meta com contagens reais. */
    const agg = p.temFilhos && !p.expandido ? agregadosAtuais.get(no.external_id) : undefined;
    const capsula = !!(agg && agg.total > 0);
    g.appendChild(el('rect', { class: 'cartao', width: p.w, height: p.h, rx: capsula ? 16 : 10 }));
    if (!capsula) {
      g.appendChild(el('line', {
        class: 'faixa', x1: 9, y1: 12, x2: 9, y2: p.h - 12, stroke: COR_ST[no.st],
      }));
    }
    const px = capsula ? 14 : 18;
    const maxCh = capsula ? 22 : 24;
    const titulo = el('text', { class: 'titulo', x: px, y: 22 });
    titulo.textContent = no.name.length > maxCh ? no.name.slice(0, maxCh - 1) + '…' : no.name;
    const ttNome = el('title');
    ttNome.textContent = no.name + ' · ' + no.st;
    titulo.appendChild(ttNome);
    g.appendChild(titulo);

    if (!capsula) {
      const tipo = el('text', { class: 'tp', x: px, y: 37 });
      const partes: string[] = [no.st];
      const rotuloDoTipo = rotuloTipo(no.node_type);
      if (rotuloDoTipo) partes.push(rotuloDoTipo);
      /* o rotulo de APRESENTACAO vive nesta linha (artefato: "h06 · anatomia
         auditada"), nao como texto flutuante que invadia o cartao vizinho */
      const seloLinha = seloDe(no.external_id);
      if (seloLinha) partes.push(seloLinha);
      const linhaTipo = partes.join(' · ');
      const maxTipo = Math.floor((p.w - px * 2) / 5.3);
      tipo.textContent = linhaTipo.length > maxTipo ? linhaTipo.slice(0, maxTipo - 1) + '…' : linhaTipo;
      if (seloLinha) {
        const ttTipo = el('title');
        ttTipo.textContent = linhaTipo;
        tipo.appendChild(ttTipo);
      }
      g.appendChild(tipo);
      const bits: string[] = [];
      if (no.know.length) bits.push(`◆ ${no.know.length}`);
      if (no.gaps.length) bits.push(`◇ ${no.gaps.length}`);
      /* ramo fechado sem agregado ainda diz quantos descendentes esconde */
      if (p.temFilhos && !p.expandido) {
        const escondidos = agregadosAtuais.get(no.external_id)?.total ?? 0;
        if (escondidos) bits.push(`+${escondidos}`);
      }
      const rodape = el('text', { class: 'meta', x: px, y: 51 });
      rodape.textContent = bits.join('  ');
      g.appendChild(rodape);
    }

    /* ZOOM SEMANTICO (next-gen): um ramo FECHADO com descendentes e uma
       CAPSULA — representa a subarvore inteira com dado real agregado:
       quantos descendentes, quanto conhecimento, quantas lacunas e a
       distribuicao epistemica. So a REPRESENTACAO muda. */
    if (agg && capsula) {
      g.classList.add('no-capsula');
      g.setAttribute('data-capsula', String(agg.total));
      const meta = el('text', { class: 'capsula-meta', x: px, y: 38 });
      meta.textContent = `${agg.total} descendentes · ${agg.know} conhec. · ${agg.gaps} lacunas${agg.tobe ? ' · ' + agg.tobe + ' TO-BE' : ''}`;
      const tt = el('title');
      tt.textContent = `${agg.total} descendente(s) · ${agg.know} conhecimento(s) · ${agg.gaps} lacuna(s)${agg.tobe ? ' · ' + agg.tobe + ' TO-BE' : ''} — clique em + para abrir`;
      meta.appendChild(tt);
      g.appendChild(meta);
      const barra = el('g', { class: 'capsula-barra', transform: `translate(${px} 48)` });
      const largura = p.w - px * 2;
      let x = 0;
      for (const st of ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE', 'MIX'] as StatusClass[]) {
        const n = agg.st[st] ?? 0;
        if (!n) continue;
        const w = Math.max(1, (largura * n) / agg.total);
        barra.appendChild(el('rect', { x, y: 0, width: w, height: 5, fill: COR_ST[st] }));
        x += w;
      }
      g.appendChild(barra);
    }

    /* Estado sem depender de texto: conhecimento e um disco cheio; lacuna e
       um anel TRACEJADO — a "sinapse aberta", algo por fechar. Os dois somem
       no zoom longe (CSS), onde so a estrutura importa. */
    /* gramatica do artefato: a LACUNA e um anel tracejado no canto superior
       direito; o CONHECIMENTO sao discos (um por item, na cor da classe) no
       canto inferior direito. Nada depende so de cor: os tooltips contam. */
    if (no.gaps.length && !capsula) {
      const gi = el('g', { class: 'ind ind-gap', transform: `translate(${p.w - 14} 14)` });
      gi.appendChild(el('circle', { r: 6, class: 'ind-gap-anel gap-ring' }));
      const tt = el('title');
      tt.textContent = `${no.gaps.length} lacuna(s) — o que este no ainda nao sabe`;
      gi.appendChild(tt);
      g.appendChild(gi);
    }
    if (no.know.length && !capsula) {
      const ki = el('g', { class: 'ind ind-know', transform: `translate(0 ${p.h - 10})` });
      no.know.slice(0, 8).forEach((k, i) => {
        ki.appendChild(el('circle', { class: 'ind-know-disco know-dot', cx: p.w - 14 - i * 9, cy: 0, r: 3, fill: COR_ST[k.st] }));
      });
      const tt = el('title');
      tt.textContent = `${no.know.length} conhecimento(s) consolidado(s)`;
      ki.appendChild(tt);
      g.appendChild(ki);
    }

    if (no.origin === 'user') {
      const marca = el('text', { class: 'marca-user', x: p.w - 8, y: 14 });
      marca.textContent = 'USER';
      g.appendChild(marca);
    }

    if (p.temFilhos) {
      const b = el('g', {
        class: 'alternar', transform: `translate(${p.w / 2} ${p.h})`,
        'data-alternar': no.external_id,
      });
      b.appendChild(el('circle', { r: 9, class: 'alternar-fundo' }));
      const sinal = el('text', { class: 'alternar-sinal', y: 4 });
      sinal.textContent = p.expandido ? '−' : '+';
      b.appendChild(sinal);
      g.appendChild(b);
    }

    /* filtros visuais leem por atributo — CSS decide, nada re-renderiza */
    if (no.know.length) g.setAttribute('data-know', '1');
    if (no.gaps.length) g.setAttribute('data-gap', '1');

    /* FUTURO pelo criterio do DOMINIO (layer tobe / st TOBE): a lente e o
       estilo de blueprint leem daqui. O selo TO-BE diz de longe o que e
       plano, sem abrir painel. */
    if (ehFuturo(no)) {
      g.setAttribute('data-futuro', '1');
      const selo = el('text', { class: 'selo-tobe', x: p.w - 8, y: no.origin === 'user' ? 26 : 14 });
      selo.textContent = 'TO-BE';
      g.appendChild(selo);
    }

    /* gramatica visual por classe (1Z.1): produto = capsula de fluxo;
       handoff = ponte (borda tracejada). CSS decide pelo atributo/classe. */
    if (ehProduto(no.external_id)) g.classList.add('no-produto');
    if (no.external_id === 'h06' || no.external_id === 'proc-handoff') g.classList.add('no-ponte');
    /* entidade EXTERNA (tipo real partner/network): gramatica propria — nao e
       estrutura interna da organizacao */
    if (no.node_type === 'partner' || no.node_type === 'network') {
      g.classList.add('no-externo');
      const se = el('text', { class: 'selo-externo', x: p.w - 8, y: no.origin === 'user' ? 26 : 14 });
      se.textContent = 'EXTERNO';
      g.appendChild(se);
    }

    /* rotulo de APRESENTACAO (h06 × proc-handoff): legenda, nao dado.
       Em CAPSULA o rotulo nao cabe: a capsula ja fala pela subarvore e o
       texto longo invadiria o cartao vizinho (achado do gate 0072). */
    const selo = capsula ? null : seloDe(no.external_id);
    if (selo) {
      /* microbadge para o LOD DISTANTE (Fase 1AB): no zoom longe o selo
         completo nao se le — os dois handoffs ganham uma versao compacta,
         escalada como os farois, para o primeiro contato nao ler o objeto
         de auditoria como estrutura organizacional. */
      if (no.external_id === 'h06' || no.external_id === 'proc-handoff') {
        const mini = el('text', {
          class: 'selo-mini ' + (no.external_id === 'h06' ? 'selo-mini-auditoria' : 'selo-mini-fluxo'),
          x: p.w / 2, y: -14, 'text-anchor': 'middle',
        });
        mini.textContent = no.external_id === 'h06' ? 'OBJETO DE AUDITORIA' : 'ETAPA DO FLUXO';
        const tt2 = el('title');
        tt2.textContent = selo;
        mini.appendChild(tt2);
        g.appendChild(mini);
      }
    }

    if (ligando === no.external_id) g.classList.add('ligando-origem');
    /* conflito de layout resolvido pelo motor (irmaos que se sobrepunham):
       marcado no DOM para teste/inspecao; a posicao ja vem afastada */
    if (p.conflito) g.setAttribute('data-conflito', '1');
    return g;
  }

  function render(entrada: EntradaRender): void {
    /* A assinatura e a lista COMPLETA das entradas de calcularLayout: se ela
       nao muda, o layout nao muda. Os AJUSTES manuais fazem parte dela
       (FOM-ARQ-0049): sem isso, arrastar/desfazer/aplicar posicao deixava
       `layoutAtual` obsoleto — cartao movido so no DOM, arestas, minimapa e
       camera presos no lugar velho, e o arrasto seguinte partindo de onde o
       no NAO estava. Serializada em ordem de chave para nao depender da
       ordem em que as posicoes chegaram. */
    const ajustes = entrada.layout.ajustes ?? {};
    const assinatura = JSON.stringify({
      n: entrada.nos.map((n) => [n.external_id, n.name, n.st, n.parent_external_id, n.ordinal, n.child_kind, n.know.length, n.gaps.length]),
      e: [...entrada.layout.expandidos].sort(),
      f: entrada.layout.filtroSt ? [...entrada.layout.filtroSt].sort() : null,
      b: entrada.layout.mostrarBase,
      u: entrada.layout.mostrarUser,
      r: entrada.relacoes.map((r) => r.id).sort(),
      l: entrada.ligando,
      a: Object.keys(ajustes).sort().map((k) => [k, ajustes[k]!.x, ajustes[k]!.y]),
    });
    /* Em modo de ligacao o cartao e alvo de CLIQUE, nao de arrasto: sem isto,
       tentar conectar dois nos moveria um deles. */
    ligandoAtual = entrada.ligando;
    relacoesAtuais = entrada.relacoes;

    const rerender = assinatura !== assinaturaArvore;
    if (rerender) {
      assinaturaArvore = assinatura;
      layoutAtual = calcularLayout(entrada.nos, entrada.layout);

      gArestas.textContent = '';
      gRelacoes.textContent = '';
      gNos.textContent = '';

      /* arestas de hierarquia — a que leva a um filho FUTURO e futura */
      for (const p of layoutAtual.posicoes) {
        const pai = p.no.parent_external_id
          ? layoutAtual.porId.get(p.no.parent_external_id)
          : undefined;
        if (!pai) continue;
        gArestas.appendChild(el('path', {
          class: 'aresta' + (p.no.child_kind === 'flow' ? ' aresta-fluxo' : '')
            + (ehFuturo(p.no) ? ' aresta-futura' : ''),
          d: curva(pai.x, pai.y + pai.h, p.x, p.y),
        }));
      }

      /* pais-ancora do futuro: na lente MUDANCAS eles ficam meio visiveis
         para o plano nao flutuar sem contexto */
      const ancoras = new Set<string>();
      for (const p of layoutAtual.posicoes) {
        if (ehFuturo(p.no) && p.no.parent_external_id) ancoras.add(p.no.parent_external_id);
      }

      /* ANCORAGEM POR ANCESTRAL VISIVEL (gate 0072, artefato): se uma ponta
         esta recolhida dentro de uma capsula, a relacao se liga a CAPSULA em
         vez de sumir — a leitura transversal sobrevive ao zoom semantico.
         Quando as duas pontas caem no mesmo ancestral, a linha e omitida
         (seria um laco dentro do proprio bloco). */
      const porIdNos = new Map(entrada.nos.map((n) => [n.external_id, n]));
      const layoutRel = layoutAtual;
      const ancora = (id: string): Layout['posicoes'][number] | undefined => {
        let atual = porIdNos.get(id);
        let guarda = 0;
        while (atual && guarda++ < 64) {
          const p = layoutRel.porId.get(atual.external_id);
          if (p) return p;
          atual = atual.parent_external_id ? porIdNos.get(atual.parent_external_id) : undefined;
        }
        return undefined;
      };

      /* relacoes explicitas — sao outra leitura, e por isso outro traco */
      for (const r of entrada.relacoes) {
        const a = ancora(r.from);
        const b = ancora(r.to);
        if (!a || !b || a === b) continue;
        /* uma relacao NOVA se desenha na tela — feedback de que o mapa vive */
        const nova = idsRelacoesVistas.size > 0 && !idsRelacoesVistas.has(r.id);
        const aFut = layoutAtual.porId.get(r.from)?.no;
        const bFut = layoutAtual.porId.get(r.to)?.no;
        const futura = (aFut && ehFuturo(aFut)) || (bFut && ehFuturo(bFut));
        const d = arco(a.x, a.y, a.h, b.x, b.y, b.h);
        /* SUPORTE/INTEGRACAO/SISTEMA sao transversais de outra natureza: o
           artefato as distingue por tracejado, lendo o TIPO verbatim */
        const suporte = /suporte|integra|sistema/i.test(r.type);
        const caminho = el('path', {
          class: 'relacao relacao-' + r.kind + (suporte ? ' relacao-suporte' : '')
            + (r.origin === 'user' ? ' relacao-user' : '')
            + (nova ? ' relacao-nova' : '') + (futura ? ' relacao-futura' : ''),
          d,
          'marker-end': 'url(#seta)',
          'data-relacao': r.id,
          /* pontas no DOM: localizacao estavel para teste e inspecao, sem
             depender do texto do tooltip (que fala com o humano, por nome) */
          'data-de': r.from,
          'data-para': r.to,
        });
        const t = el('title');
        t.textContent = `${a.no.name} — ${r.type} → ${b.no.name}`;
        caminho.appendChild(t);
        /* grupo por relacao: a linha visivel + um ALVO largo e invisivel
           (gate 0072) — a conexao e clicavel sem exigir mira de 1.4px */
        const grupo = el('g', {
          class: 'relacao-grupo', 'data-relacao': r.id, tabindex: 0, role: 'button',
          'aria-label': `conexao ${r.type}: ${a.no.name} para ${b.no.name}`,
        });
        grupo.appendChild(el('path', { class: 'relacao-alvo', d }));
        grupo.appendChild(caminho);
        /* o TIPO da relacao no apice do arco — some no zoom longe (CSS) */
        const rot = el('text', {
          class: 'relacao-rotulo', x: (a.x + b.x) / 2,
          y: Math.min(a.y, b.y) - 40 - Math.abs(a.x - b.x) * 0.08 + 10,
          'text-anchor': 'middle',
        });
        rot.textContent = r.type.length > 28 ? r.type.slice(0, 27) + '…' : r.type;
        grupo.appendChild(rot);
        gRelacoes.appendChild(grupo);
      }

      idsRelacoesVistas = new Set(entrada.relacoes.map((r) => r.id));

      agregadosAtuais = agregarSubarvores(entrada.nos);
      for (const p of layoutAtual.posicoes) {
        const g = desenharNo(p, entrada.ligando);
        if (ancoras.has(p.no.external_id)) g.setAttribute('data-ancora', '1');
        gNos.appendChild(g);
      }
      pintarMinimapaNos();
      pintarMinimapaViewport();

      /* DELEGACAO (1AD.1): nenhum listener por no — os dois handlers unicos
         de gNos (montados uma vez em criarMapa) resolvem o alvo por
         closest('.no'). Re-render deixa de recriar listeners e o custo
         para de crescer com o mapa. */
    }

    /* Um re-render recria os cartoes: a classe `selecionado` morre com o DOM
       antigo. Sem esquecer o valor anterior, o `if` abaixo achava que nada
       mudou e o no ficava selecionado no store mas APAGADO no mapa (o foco
       esmaecia todo mundo, inclusive ele). Achado do gate 0072. */
    if (rerender) selecionadoAtual = null;
    if (selecionadoAtual !== entrada.selecionado) {
      if (selecionadoAtual) {
        gNos.querySelector(seletorPorId(selecionadoAtual))?.classList.remove('selecionado');
      }
      if (entrada.selecionado) {
        gNos.querySelector(seletorPorId(entrada.selecionado))?.classList.add('selecionado');
      }
      selecionadoAtual = entrada.selecionado;
    }
    /* conexao selecionada: a linha acende e as duas pontas ganham presenca */
    relacaoSelecionadaAtual = entrada.relacaoSelecionada ?? null;
    for (const g of Array.from(gRelacoes.children)) {
      const sel = (g as SVGGElement).dataset.relacao === relacaoSelecionadaAtual;
      g.classList.toggle('relacao-grupo-selecionado', sel);
      g.querySelector('.relacao')?.classList.toggle('relacao-selecionada', sel);
    }
    /* radar: marca a posicao do contexto atual */
    const m = escalaMini();
    const pSel = selecionadoAtual ? layoutAtual?.porId.get(selecionadoAtual) : null;
    if (m && pSel) {
      miniMarcador.setAttribute('cx', String(pSel.x * m.k + m.ox));
      miniMarcador.setAttribute('cy', String((pSel.y + pSel.h / 2) * m.k + m.oy));
    } else {
      miniMarcador.setAttribute('cx', '-10');
      miniMarcador.setAttribute('cy', '-10');
    }
    aplicarFoco(selecionadoAtual);
  }

  /* O MAPA INTEIRO responde a selecao: vizinhos diretos (pai, filhos e pontas
     de relacao) ganham presenca, o resto recua discretamente, e as relacoes
     que tocam o no ficam evidentes. E o "onde estou" respondido pelo proprio
     mapa, sem o usuario procurar. Tudo por classe — re-render nenhum. */
  function aplicarFoco(sel: string | null): void {
    const relSel = relacaoSelecionadaAtual
      ? relacoesAtuais.find((r) => r.id === relacaoSelecionadaAtual) ?? null : null;
    svg.classList.toggle('com-foco', sel !== null || relSel !== null);
    if (!layoutAtual) return;
    const vizinhos = new Set<string>();
    if (sel) {
      const p = layoutAtual.porId.get(sel);
      if (p?.no.parent_external_id) vizinhos.add(p.no.parent_external_id);
      for (const q of layoutAtual.posicoes) {
        if (q.no.parent_external_id === sel) vizinhos.add(q.no.external_id);
      }
      for (const r of relacoesAtuais) {
        if (r.from === sel) vizinhos.add(r.to);
        if (r.to === sel) vizinhos.add(r.from);
      }
    }
    /* conexao selecionada: as DUAS pontas sao a vizinhanca */
    if (relSel) { vizinhos.add(relSel.from); vizinhos.add(relSel.to); }
    for (const g of Array.from(gNos.children)) {
      const id = (g as SVGGElement).dataset.id!;
      g.classList.toggle('no-vizinho', vizinhos.has(id));
    }
    for (const c of Array.from(gRelacoes.querySelectorAll('.relacao'))) {
      const caminho = c as SVGPathElement;
      const ativa = (sel !== null && relacoesAtuais.some((r) =>
        r.id === caminho.dataset.relacao && (r.from === sel || r.to === sel)))
        || (relSel !== null && caminho.dataset.relacao === relSel.id);
      caminho.classList.toggle('relacao-ativa', ativa);
    }
  }

  /* ------------------------------------------------------ camera animada --
     O futurista vem da INTERACAO: a camera se move, nao corta. Transicao
     curta (340ms, easeInOutCubic), cancelavel por novo destino, e reduzida a
     salto instantaneo quando o usuario pede menos movimento
     (prefers-reduced-motion) ou quando nao ha rAF (testes). */
  const MOVIMENTO_REDUZIDO = typeof matchMedia !== 'undefined'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;
  let animacaoCamera: number | null = null;

  function irCameraPara(zAlvo: number, pxAlvo: number, pyAlvo: number, dur = 340): void {
    if (animacaoCamera !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(animacaoCamera);
      animacaoCamera = null;
    }
    const semAnimacao = MOVIMENTO_REDUZIDO || dur <= 0
      || typeof requestAnimationFrame === 'undefined' || typeof performance === 'undefined';
    if (semAnimacao) {
      zoom = zAlvo; panX = pxAlvo; panY = pyAlvo;
      aplicarTransform();
      return;
    }
    const z0 = zoom, x0 = panX, y0 = panY, t0 = performance.now();
    const suaviza = (t: number): number =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const passo = (agora: number): void => {
      const t = Math.min(1, (agora - t0) / dur);
      const k = suaviza(t);
      zoom = z0 + (zAlvo - z0) * k;
      panX = x0 + (pxAlvo - x0) * k;
      panY = y0 + (pyAlvo - y0) * k;
      aplicarTransform();
      animacaoCamera = t < 1 ? requestAnimationFrame(passo) : null;
    };
    animacaoCamera = requestAnimationFrame(passo);
  }

  function ajustar(): void {
    if (!layoutAtual || layoutAtual.posicoes.length === 0) return;
    const caixa = svg.getBoundingClientRect();
    const larguraDisponivel = caixa.width || 1200;
    const alturaDisponivel = caixa.height || 800;
    /* enquadramento do artefato: folga de 80px na horizontal, 120 na
       vertical, topo em 60 e teto de 1.6 (nunca amplia alem disso) */
    const margem = 40;
    const z = Math.min(
      (larguraDisponivel - 80) / Math.max(layoutAtual.extensao.w, 1),
      (alturaDisponivel - 120) / Math.max(layoutAtual.extensao.h, 1),
      1.6,
    );
    const zAlvo = Math.max(0.12, z);
    irCameraPara(
      zAlvo,
      larguraDisponivel / 2 - (layoutAtual.extensao.x + layoutAtual.extensao.w / 2) * zAlvo,
      Math.max(margem, 60) - layoutAtual.extensao.y * zAlvo,
    );
  }

  /* Piso em que o titulo de um cartao ainda se le numa tela comum. */
  const ZOOM_LEGIVEL = 0.75;

  function apresentar(): void {
    if (!layoutAtual || layoutAtual.posicoes.length === 0) return;
    /* decide pelo zoom que o ENQUADRAR produziria, nao pelo zoom corrente:
       a camera anima, e `zoom` so chega ao alvo no fim da animacao — ler o
       valor antigo fazia o piso de legibilidade nunca ser aplicado quando o
       usuario ja estava proximo (next-gen: nivel semantico legivel sempre) */
    const caixa0 = svg.getBoundingClientRect();
    const zAjuste = Math.max(0.12, Math.min(
      ((caixa0.width || 1200) - 80) / Math.max(layoutAtual.extensao.w, 1),
      ((caixa0.height || 800) - 120) / Math.max(layoutAtual.extensao.h, 1),
      1.6,
    ));
    if (zAjuste >= ZOOM_LEGIVEL) { ajustar(); return; }
    /* nao coube legivel: centraliza o topo da arvore no piso de legibilidade */
    const raizPos = layoutAtual.posicoes.reduce((a, b) => (a.y <= b.y ? a : b));
    const caixa = svg.getBoundingClientRect();
    irCameraPara(
      ZOOM_LEGIVEL,
      (caixa.width || 1200) / 2 - raizPos.x * ZOOM_LEGIVEL,
      60 - raizPos.y * ZOOM_LEGIVEL,
    );
  }

  function centralizarEm(externalId: string): void {
    const p = layoutAtual?.porId.get(externalId);
    if (!p) return;
    const caixa = svg.getBoundingClientRect();
    irCameraPara(
      zoom,
      (caixa.width || 1200) / 2 - p.x * zoom,
      (caixa.height || 800) / 2 - (p.y + p.h / 2) * zoom,
    );
  }

  /* CLICK-TO-FOCUS: selecionar e ENTRAR. Se o usuario esta longe, a camera
     aproxima ate o piso legivel; se o no esta fora do miolo da tela, a camera
     o traz para o centro. Se ja esta perto e no centro, nada se move — foco
     nao pode virar enjoo. */
  function focarEm(externalId: string): void {
    const p = layoutAtual?.porId.get(externalId);
    if (!p) return;
    const caixa = svg.getBoundingClientRect();
    const w = caixa.width || 1200, h = caixa.height || 800;
    const zAlvo = zoom < 0.8 ? Math.min(0.95, Math.max(0.8, zoom * 1.6)) : zoom;
    const sx = p.x * zoom + panX;
    const sy = (p.y + p.h / 2) * zoom + panY;
    const foraDoMiolo = sx < w * 0.22 || sx > w * 0.78 || sy < h * 0.2 || sy > h * 0.8;
    if (zAlvo === zoom && !foraDoMiolo) return;
    irCameraPara(
      zAlvo,
      w / 2 - p.x * zAlvo,
      h / 2 - (p.y + p.h / 2) * zAlvo,
    );
  }

  /* ------------------------------------------------ camera de RAMO (0072) --
     Abrir um ramo deve sentir-se como "abri este ramo", nunca como "o mapa
     inteiro explodiu". Duas pecas, ambas sobre irCameraPara (uma so camera):
       · ancorar: depois do re-layout, o no que a pessoa clicou fica no MESMO
         ponto da tela (o resto se recompoe em volta dele);
       · enquadrarRamo: o no + filhos visiveis + pai entram na viewport com o
         MENOR movimento possivel — pan minimo se ja cabem no zoom atual,
         zoom-out limitado (nunca abaixo do piso semantico, nunca zoom-in
         alem do atual) se nao cabem. */
  function posicaoNaTela(externalId: string): { x: number; y: number } | null {
    const p = layoutAtual?.porId.get(externalId);
    if (!p) return null;
    return { x: p.x * zoom + panX, y: p.y * zoom + panY };
  }
  function ancorarEm(externalId: string, antes: { x: number; y: number } | null): void {
    if (!antes) return;
    const p = layoutAtual?.porId.get(externalId);
    if (!p) return;
    panX = antes.x - p.x * zoom;
    panY = antes.y - p.y * zoom;
    aplicarTransform();
  }
  function caixaDoRamo(externalId: string): { x: number; y: number; w: number; h: number } | null {
    if (!layoutAtual) return null;
    const p = layoutAtual.porId.get(externalId);
    if (!p) return null;
    const dentro = new Set<string>([externalId]);
    /* descendentes VISIVEIS (o layout so contem o que esta aberto) */
    let cresceu = true;
    while (cresceu) {
      cresceu = false;
      for (const q of layoutAtual.posicoes) {
        const pai = q.no.parent_external_id;
        if (pai && dentro.has(pai) && !dentro.has(q.no.external_id)) { dentro.add(q.no.external_id); cresceu = true; }
      }
    }
    /* o pai entra na caixa: contexto nunca se perde */
    if (p.no.parent_external_id && layoutAtual.porId.has(p.no.parent_external_id)) dentro.add(p.no.parent_external_id);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const q of layoutAtual.posicoes) {
      if (!dentro.has(q.no.external_id)) continue;
      minX = Math.min(minX, q.x - q.w / 2); maxX = Math.max(maxX, q.x + q.w / 2);
      minY = Math.min(minY, q.y); maxY = Math.max(maxY, q.y + q.h);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }
  const ZOOM_MIN_RAMO = 0.55;
  function enquadrarRamo(externalId: string): void {
    const c = caixaDoRamo(externalId);
    if (!c) return;
    const caixa = svg.getBoundingClientRect();
    const w = caixa.width || 1200, h = caixa.height || 800;
    const margem = Math.min(48, w * 0.06);
    const zFit = Math.min((w - margem * 2) / Math.max(c.w, 1), (h - margem * 2) / Math.max(c.h, 1));
    /* cabe no zoom atual? entao so o pan minimo que traz a caixa para dentro */
    if (zFit >= zoom) {
      const sx0 = c.x * zoom + panX, sy0 = c.y * zoom + panY;
      const sx1 = sx0 + c.w * zoom, sy1 = sy0 + c.h * zoom;
      let dx = 0, dy = 0;
      if (sx0 < margem) dx = margem - sx0; else if (sx1 > w - margem) dx = (w - margem) - sx1;
      if (sy0 < margem) dy = margem - sy0; else if (sy1 > h - margem) dy = (h - margem) - sy1;
      if (dx === 0 && dy === 0) return;
      irCameraPara(zoom, panX + dx, panY + dy);
      return;
    }
    /* nao cabe: afasta so o necessario, sem cruzar o piso do ramo */
    const zAlvo = Math.max(ZOOM_MIN_RAMO, Math.min(zoom, zFit));
    irCameraPara(zAlvo, w / 2 - (c.x + c.w / 2) * zAlvo, margem - c.y * zAlvo);
  }

  aplicarTransform();

  return {
    render,
    ajustar,
    apresentar,
    centralizarEm,
    focarEm,
    posicaoNaTela,
    ancorarEm,
    enquadrarRamo,
    caixaDoRamo,
    /* o zoom que ENQUADRAR produziria agora — quem escolhe a representacao
       (nivel semantico) pergunta antes de decidir, sem mover a camera */
    zoomDeAjuste(): number {
      if (!layoutAtual || layoutAtual.posicoes.length === 0) return 1;
      const caixa = svg.getBoundingClientRect();
      return Math.max(0.12, Math.min(
        ((caixa.width || 1200) - 80) / Math.max(layoutAtual.extensao.w, 1),
        ((caixa.height || 800) - 120) / Math.max(layoutAtual.extensao.h, 1),
        1.6,
      ));
    },
    get zoomLegivel(): number { return ZOOM_LEGIVEL; },
    /* mostrar/esconder as relacoes transversais — o toggle da referencia */
    mostrarRelacoes(v: boolean): void {
      (gRelacoes as SVGGElement).style.display = v ? '' : 'none';
    },
    /* ENTRAR na lente FUTURA/MUDANCAS e pedir para ver o futuro: o conjunto
       inteiro e enquadrado na entrada (um farol visivel nao e o plano todo —
       o minimapa provou isso na auditoria). Dentro da lente, pan e zoom do
       usuario ficam em paz: so o CLIQUE no seletor enquadra de novo. */
    enquadrarFuturoSePreciso(): void {
      if (!layoutAtual) return;
      const fut = layoutAtual.posicoes.filter((p) => ehFuturo(p.no));
      if (!fut.length) return;
      const caixa = svg.getBoundingClientRect();
      const minX = Math.min(...fut.map((p) => p.x - p.w / 2));
      const maxX = Math.max(...fut.map((p) => p.x + p.w / 2));
      const minY = Math.min(...fut.map((p) => p.y));
      const maxY = Math.max(...fut.map((p) => p.y + p.h));
      const z = Math.min(((caixa.width || 1200) - 140) / Math.max(maxX - minX, 1),
                         ((caixa.height || 800) - 140) / Math.max(maxY - minY, 1), 1.1);
      const zAlvo = Math.max(0.55, z);
      irCameraPara(
        zAlvo,
        (caixa.width || 1200) / 2 - ((minX + maxX) / 2) * zAlvo,
        (caixa.height || 800) / 2 - ((minY + maxY) / 2) * zAlvo,
      );
    },
    /* As quatro lentes: um atributo no svg, CSS decide. Zoom, pan, selecao e
       layout ficam EXATAMENTE onde estavam — e o ponto. */
    setLente(l: Lente): void {
      svg.setAttribute('data-lente', l);
    },
    get lente(): Lente {
      return (svg.getAttribute('data-lente') as Lente) ?? 'completa';
    },
    /* foco visual: 'sabido' apaga quem nao tem know; 'lacunas', quem nao tem
       gap. E lente, nao filtro de dados: nada sai do DOM. */
    modoFoco(modo: 'sabido' | 'lacunas' | null): void {
      svg.classList.toggle('foco-sabido', modo === 'sabido');
      svg.classList.toggle('foco-lacunas', modo === 'lacunas');
    },
    get zoomAtual(): number { return zoom; },
    zoomPor(fator: number): void {
      if (fator < 1 && zoom * fator < ZOOM_SEMANTICO && zoom >= ZOOM_SEMANTICO
        && opcoes.aoZoomSemantico?.('afastar') === true) return;
      zoom = Math.min(2.5, Math.max(0.12, zoom * fator));
      aplicarTransform();
    },
    get layout(): Layout | null { return layoutAtual; },
    get svg(): SVGSVGElement { return svg; },
  };
}

export type Mapa = ReturnType<typeof criarMapa>;
